import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/axios';

const SitePhotos = () => {
  const [photos, setPhotos] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('all');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [filterPhase, setFilterPhase] = useState('all');
  const [filterVisibility, setFilterVisibility] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // grid or timeline

  // Form state for new photo
  const [newPhoto, setNewPhoto] = useState({
    project_id: '',
    phase: '',
    caption: '',
    description: '',
    date_taken: '',
    featured: false,
    customer_visible: true,
    internal_notes: '',
    files: []
  });

  // Form state for editing photo
  const [editPhoto, setEditPhoto] = useState({});

  useEffect(() => {
    fetchPhotos();
    fetchProjects();
  }, [selectedProject]);

  const fetchPhotos = async () => {
    try {
      setLoading(true);
      setError(null);
      const url = selectedProject === 'all' 
        ? '/admin/site-photos' 
        : `/admin/site-photos?project_id=${selectedProject}`;
      const response = await api.get(url);
      setPhotos(response.data.data);
    } catch (err) {
      console.error('Failed to fetch photos:', err);
      setError('Failed to load photos. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await api.get('/admin/projects');
      setProjects(response.data.data);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    }
  };

  const handlePhotoUpload = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('project_id', newPhoto.project_id);
      formData.append('phase', newPhoto.phase);
      formData.append('caption', newPhoto.caption);
      formData.append('description', newPhoto.description);
      formData.append('date_taken', newPhoto.date_taken);
      formData.append('featured', newPhoto.featured);
      formData.append('customer_visible', newPhoto.customer_visible);
      formData.append('internal_notes', newPhoto.internal_notes);
      
      newPhoto.files.forEach((file, index) => {
        formData.append('files', file);
      });

      const response = await api.post('/admin/site-photos', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setPhotos(prev => [response.data.data, ...prev]);
      setShowUploadModal(false);
      setNewPhoto({
        project_id: '',
        phase: '',
        caption: '',
        description: '',
        date_taken: '',
        featured: false,
        customer_visible: true,
        internal_notes: '',
        files: []
      });
    } catch (err) {
      console.error('Failed to upload photos:', err);
      alert('Failed to upload photos. Please try again.');
    }
  };

  const handleUpdatePhoto = async (e) => {
    e.preventDefault();
    try {
      const response = await api.put(`/admin/site-photos/${editPhoto.id}`, editPhoto);
      setPhotos(prev => prev.map(p => p.id === editPhoto.id ? response.data.data : p));
      setShowEditModal(false);
      setSelectedPhoto(null);
    } catch (err) {
      console.error('Failed to update photo:', err);
      alert('Failed to update photo. Please try again.');
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/admin/site-photos/${photoId}`);
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      if (selectedPhoto?.id === photoId) {
        setSelectedPhoto(null);
      }
    } catch (err) {
      console.error('Failed to delete photo:', err);
      alert('Failed to delete photo. Please try again.');
    }
  };

  const toggleCustomerVisibility = async (photoId, visible) => {
    try {
      const photo = photos.find(p => p.id === photoId);
      const updatedPhoto = { ...photo, customer_visible: visible };
      const response = await api.put(`/admin/site-photos/${photoId}`, updatedPhoto);
      setPhotos(prev => prev.map(p => p.id === photoId ? response.data.data : p));
    } catch (err) {
      console.error('Failed to update visibility:', err);
      alert('Failed to update visibility. Please try again.');
    }
  };

  const toggleFeatured = async (photoId, featured) => {
    try {
      const photo = photos.find(p => p.id === photoId);
      const updatedPhoto = { ...photo, featured: featured };
      const response = await api.put(`/admin/site-photos/${photoId}`, updatedPhoto);
      setPhotos(prev => prev.map(p => p.id === photoId ? response.data.data : p));
    } catch (err) {
      console.error('Failed to update featured status:', err);
      alert('Failed to update featured status. Please try again.');
    }
  };

  const openEditModal = (photo) => {
    setEditPhoto(photo);
    setShowEditModal(true);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setNewPhoto({ ...newPhoto, files });
  };

  const getVisibilityIcon = (visible) => {
    return visible ? '👁️' : '🚫';
  };

  const getFeaturedIcon = (featured) => {
    return featured ? '⭐' : '☆';
  };

  const filteredPhotos = photos.filter(photo => {
    const matchesPhase = filterPhase === 'all' || photo.phase === filterPhase;
    const matchesVisibility = filterVisibility === 'all' || 
      (filterVisibility === 'visible' && photo.customer_visible) ||
      (filterVisibility === 'hidden' && !photo.customer_visible);
    const matchesSearch = photo.caption?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         photo.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         photo.project_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesPhase && matchesVisibility && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-palamuru-blue mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Site Photos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Photos</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-palamuru-gold text-white px-4 py-2 rounded-lg hover:bg-palamuru-gold-dark transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display-bold text-gray-900">Site Photo Manager</h1>
          <p className="text-gray-600 mt-1">Upload and manage site photos with full control over visibility and featured status.</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}
              title="Grid View"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('timeline')}
              className={`p-2 rounded-lg ${viewMode === 'timeline' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}
              title="Timeline View"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </button>
          </div>
          <button
            onClick={() => setShowUploadModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-body-semibold text-sm flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <span>Upload Photos</span>
          </button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-6 rounded-2xl shadow-soft border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-body-semibold text-gray-700 mb-2">Search Photos</label>
            <input
              type="text"
              placeholder="Search by caption, description, or project..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-body-semibold text-gray-700 mb-2">Filter by Project</label>
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Projects</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-body-semibold text-gray-700 mb-2">Filter by Phase</label>
            <select
              value={filterPhase}
              onChange={(e) => setFilterPhase(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Phases</option>
              <option value="foundation">Foundation</option>
              <option value="structure">Structure</option>
              <option value="electrical">Electrical</option>
              <option value="plumbing">Plumbing</option>
              <option value="finishing">Finishing</option>
              <option value="completion">Completion</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-body-semibold text-gray-700 mb-2">Filter by Visibility</label>
            <select
              value={filterVisibility}
              onChange={(e) => setFilterVisibility(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Photos</option>
              <option value="visible">Customer Visible</option>
              <option value="hidden">Internal Only</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedProject('all');
                setFilterPhase('all');
                setFilterVisibility('all');
              }}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Photos Grid/Timeline */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredPhotos.map(photo => (
            <div key={photo.id} className="bg-white rounded-2xl shadow-soft border border-gray-100 overflow-hidden hover:shadow-medium transition-shadow duration-200">
              {/* Photo */}
              <div className="relative aspect-square bg-gray-100">
                <img
                  src={photo.file_url && photo.file_url.startsWith('http') ? photo.file_url : 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik02MCA4MEM2MCA3MS4xNjM0IDY3LjE2MzQgNjQgNzYgNjRIMTI0QzEzMi44MzcgNjQgMTQwIDcxLjE2MzQgMTQwIDgwVjEyMEMxNDAgMTI4LjgzNyAxMzIuODM3IDEzNiAxMjQgMTM2SDc2QzY3LjE2MzQgMTM2IDYwIDEyOC44MzcgNjAgMTIwVjgwWiIgZmlsbD0iI0QxRDVEM0EiLz4KPHBhdGggZD0iTTgwIDEwMEM4MCA5NS41ODE3IDgzLjU4MTcgOTIgODggOTJIMTEyQzExNi40MTggOTIgMTIwIDk1LjU4MTcgMTIwIDEwMFYxMjBDMTIwIDEyNC40MTggMTE2LjQxOCAxMjggMTEyIDEyOEg4OEM4My41ODE3IDEyOCA4MCAxMjQuNDE4IDgwIDEyMFYxMDBaIiBmaWxsPSIjOUI5QkEwIi8+Cjwvc3ZnPgo='}
                  alt={photo.caption || 'Site photo'}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik02MCA4MEM2MCA3MS4xNjM0IDY3LjE2MzQgNjQgNzYgNjRIMTI0QzEzMi44MzcgNjQgMTQwIDcxLjE2MzQgMTQwIDgwVjEyMEMxNDAgMTI4LjgzNyAxMzIuODM3IDEzNiAxMjQgMTM2SDc2QzY3LjE2MzQgMTM2IDYwIDEyOC44MzcgNjAgMTIwVjgwWiIgZmlsbD0iI0QxRDVEM0EiLz4KPHBhdGggZD0iTTgwIDEwMEM4MCA5NS41ODE3IDgzLjU4MTcgOTIgODggOTJIMTEyQzExNi40MTggOTIgMTIwIDk1LjU4MTcgMTIwIDEwMFYxMjBDMTIwIDEyNC40MTggMTE2LjQxOCAxMjggMTEyIDEyOEg4OEM4My41ODE3IDEyOCA4MCAxMjQuNDE4IDgwIDEyMFYxMDBaIiBmaWxsPSIjOUI5QkEwIi8+Cjwvc3ZnPgo=';
                  }}
                />
                
                {/* Overlay with actions */}
                <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                  <div className="opacity-0 hover:opacity-100 transition-opacity duration-200 flex space-x-2">
                    <button
                      onClick={() => openEditModal(photo)}
                      className="p-2 bg-white rounded-full text-gray-700 hover:text-blue-600 transition-colors"
                      title="Edit Photo"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeletePhoto(photo.id)}
                      className="p-2 bg-white rounded-full text-gray-700 hover:text-red-600 transition-colors"
                      title="Delete Photo"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Status badges */}
                <div className="absolute top-2 left-2 flex space-x-1">
                  <button
                    onClick={() => toggleFeatured(photo.id, !photo.featured)}
                    className={`p-1 rounded-full text-sm ${photo.featured ? 'bg-yellow-400 text-yellow-900' : 'bg-gray-200 text-gray-600'}`}
                    title={photo.featured ? 'Remove from featured' : 'Mark as featured'}
                  >
                    {getFeaturedIcon(photo.featured)}
                  </button>
                  <button
                    onClick={() => toggleCustomerVisibility(photo.id, !photo.customer_visible)}
                    className={`p-1 rounded-full text-sm ${photo.customer_visible ? 'bg-green-400 text-green-900' : 'bg-red-400 text-red-900'}`}
                    title={photo.customer_visible ? 'Hide from customer' : 'Show to customer'}
                  >
                    {getVisibilityIcon(photo.customer_visible)}
                  </button>
                </div>
              </div>

              {/* Photo Details */}
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-display-semibold text-gray-900 text-sm line-clamp-1">{photo.caption || 'Untitled'}</h3>
                </div>
                
                <div className="space-y-2 text-xs text-gray-600">
                  <div>
                    <span className="font-medium">Project:</span> {photo.project_name || 'N/A'}
                  </div>
                  {photo.phase && (
                    <div>
                      <span className="font-medium">Phase:</span> {photo.phase}
                    </div>
                  )}
                  <div>
                    <span className="font-medium">Date:</span> {new Date(photo.date_taken || photo.created_at).toLocaleDateString()}
                  </div>
                </div>

                {photo.description && (
                  <p className="text-xs text-gray-500 mt-2 line-clamp-2">{photo.description}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Timeline View */
        <div className="space-y-6">
          {filteredPhotos.map(photo => (
            <div key={photo.id} className="bg-white rounded-2xl shadow-soft border border-gray-100 p-6">
              <div className="flex space-x-4">
                <div className="flex-shrink-0">
                  <img
                    src={photo.file_url && photo.file_url.startsWith('http') ? photo.file_url : 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik02MCA4MEM2MCA3MS4xNjM0IDY3LjE2MzQgNjQgNzYgNjRIMTI0QzEzMi44MzcgNjQgMTQwIDcxLjE2MzQgMTQwIDgwVjEyMEMxNDAgMTI4LjgzNyAxMzIuODM3IDEzNiAxMjQgMTM2SDc2QzY3LjE2MzQgMTM2IDYwIDEyOC44MzcgNjAgMTIwVjgwWiIgZmlsbD0iI0QxRDVEM0EiLz4KPHBhdGggZD0iTTgwIDEwMEM4MCA5NS41ODE3IDgzLjU4MTcgOTIgODggOTJIMTEyQzExNi40MTggOTIgMTIwIDk1LjU4MTcgMTIwIDEwMFYxMjBDMTIwIDEyNC40MTggMTE2LjQxOCAxMjggMTEyIDEyOEg4OEM4My41ODE3IDEyOCA4MCAxMjQuNDE4IDgwIDEyMFYxMDBaIiBmaWxsPSIjOUI5QkEwIi8+Cjwvc3ZnPgo='}
                    alt={photo.caption || 'Site photo'}
                    className="w-32 h-32 object-cover rounded-lg"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik02MCA4MEM2MCA3MS4xNjM0IDY3LjE2MzQgNjQgNzYgNjRIMTI0QzEzMi44MzcgNjQgMTQwIDcxLjE2MzQgMTQwIDgwVjEyMEMxNDAgMTI4LjgzNyAxMzIuODM3IDEzNiAxMjQgMTM2SDc2QzY3LjE2MzQgMTM2IDYwIDEyOC44MzcgNjAgMTIwVjgwWiIgZmlsbD0iI0QxRDVEM0EiLz4KPHBhdGggZD0iTTgwIDEwMEM4MCA5NS41ODE3IDgzLjU4MTcgOTIgODggOTJIMTEyQzExNi40MTggOTIgMTIwIDk1LjU4MTcgMTIwIDEwMFYxMjBDMTIwIDEyNC40MTggMTE2LjQxOCAxMjggMTEyIDEyOEg4OEM4My41ODE3IDEyOCA4MCAxMjQuNDE4IDgwIDEyMFYxMDBaIiBmaWxsPSIjOUI5QkEwIi8+Cjwvc3ZnPgo=';
                    }}
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-display-semibold text-lg text-gray-900">{photo.caption || 'Untitled'}</h3>
                      <p className="text-sm text-gray-600">{photo.project_name} • {photo.phase || 'General'}</p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => toggleFeatured(photo.id, !photo.featured)}
                        className={`p-2 rounded-lg ${photo.featured ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-600'}`}
                        title={photo.featured ? 'Remove from featured' : 'Mark as featured'}
                      >
                        {getFeaturedIcon(photo.featured)}
                      </button>
                      <button
                        onClick={() => toggleCustomerVisibility(photo.id, !photo.customer_visible)}
                        className={`p-2 rounded-lg ${photo.customer_visible ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                        title={photo.customer_visible ? 'Hide from customer' : 'Show to customer'}
                      >
                        {getVisibilityIcon(photo.customer_visible)}
                      </button>
                      <button
                        onClick={() => openEditModal(photo)}
                        className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                        title="Edit Photo"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDeletePhoto(photo.id)}
                        className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                        title="Delete Photo"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  <p className="text-gray-700 mb-3">{photo.description || 'No description provided.'}</p>
                  
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>Uploaded: {new Date(photo.created_at).toLocaleDateString()}</span>
                    <span>Date Taken: {new Date(photo.date_taken || photo.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {filteredPhotos.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">📸</div>
          <h3 className="text-lg font-display-semibold text-gray-900 mb-2">No photos found</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm || selectedProject !== 'all' || filterPhase !== 'all' || filterVisibility !== 'all'
              ? 'Try adjusting your search or filter criteria.'
              : 'Get started by uploading your first site photo.'
            }
          </p>
          {!searchTerm && selectedProject === 'all' && filterPhase === 'all' && filterVisibility === 'all' && (
            <button
              onClick={() => setShowUploadModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Upload First Photo
            </button>
          )}
        </div>
      )}

      {/* Upload Photos Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Upload Site Photos</h2>
            <form onSubmit={handlePhotoUpload} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
                  <select
                    value={newPhoto.project_id}
                    onChange={(e) => setNewPhoto({...newPhoto, project_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Select Project</option>
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>{project.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phase (Optional)</label>
                  <select
                    value={newPhoto.phase}
                    onChange={(e) => setNewPhoto({...newPhoto, phase: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Phase</option>
                    <option value="foundation">Foundation</option>
                    <option value="structure">Structure</option>
                    <option value="electrical">Electrical</option>
                    <option value="plumbing">Plumbing</option>
                    <option value="finishing">Finishing</option>
                    <option value="completion">Completion</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Caption</label>
                <input
                  type="text"
                  value={newPhoto.caption}
                  onChange={(e) => setNewPhoto({...newPhoto, caption: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Brief description of the photo"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newPhoto.description}
                  onChange={(e) => setNewPhoto({...newPhoto, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Detailed description of what's shown in the photo"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date Taken</label>
                  <input
                    type="date"
                    value={newPhoto.date_taken}
                    onChange={(e) => setNewPhoto({...newPhoto, date_taken: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Internal Notes</label>
                  <input
                    type="text"
                    value={newPhoto.internal_notes}
                    onChange={(e) => setNewPhoto({...newPhoto, internal_notes: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Internal notes (not visible to customer)"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Photos</label>
                <input
                  type="file"
                  onChange={handleFileChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                  multiple
                  accept="image/*"
                />
                <p className="text-xs text-gray-500 mt-1">
                  You can select multiple photos. Supported formats: JPG, PNG, GIF
                </p>
              </div>

              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="photoCustomerVisible"
                    checked={newPhoto.customer_visible}
                    onChange={(e) => setNewPhoto({...newPhoto, customer_visible: e.target.checked})}
                    className="rounded border-gray-300"
                  />
                  <label htmlFor="photoCustomerVisible" className="text-sm text-gray-700">
                    Visible to Customer
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="photoFeatured"
                    checked={newPhoto.featured}
                    onChange={(e) => setNewPhoto({...newPhoto, featured: e.target.checked})}
                    className="rounded border-gray-300"
                  />
                  <label htmlFor="photoFeatured" className="text-sm text-gray-700">
                    Mark as Featured
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Upload Photos
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SitePhotos; 